/**
 * @fileoverview Password-recovery API calls.
 * @description The three password-recovery calls, through the shared Axios client:
 * POST /identity/password/forgot (email a reset link), POST /identity/password/reset/verify
 * (confirm the link's token is still usable, before showing the new-password form) and
 * POST /identity/password/reset (set the new password; every session is revoked and no session is
 * issued, so the user logs in again). For forgot, the backend always reports success regardless of whether the identifier is registered
 * (no account-enumeration surface), so the form has no "unknown email" error state to design for —
 * requestAccepted is always true in practice; there's no documented case where it isn't. Also sends
 * this device's ID, attached internally rather than by the caller (matching login/verify/resend),
 * even though the backend's DTO marks it optional — there's no reason to omit a value we always
 * have on hand.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { httpClient } from '@/shared/api/http-client'
import { getOrCreateDeviceId } from '@/shared/lib/device/device-identity.utils'

export interface PasswordResetRequest {
  email: string
}

interface PasswordResetRequestBody {
  identifier: string
  clientDeviceId: string
}

export interface PasswordResetResponse {
  requestAccepted: true
}

export interface VerifyPasswordResetRequest {
  token: string
}

export interface VerifyPasswordResetResponse {
  verified: true
}

export interface ResetPasswordRequest {
  token: string
  newPassword: string
  confirmPassword: string
}

export interface ResetPasswordResponse {
  passwordReset: true
  /** A fresh Login + MFA is required. */
  loginRequired: true
}

/**
 * @description Requests a password-reset link for the given email address.
 * @param {PasswordResetRequest} payload - The email address entered on the forgot-password form.
 * @returns {Promise<PasswordResetResponse>} Whether the request was accepted.
 */
export async function requestPasswordReset(
  payload: PasswordResetRequest,
): Promise<PasswordResetResponse> {
  const body: PasswordResetRequestBody = {
    identifier: payload.email,
    clientDeviceId: getOrCreateDeviceId(),
  }
  const { data } = await httpClient.post<PasswordResetResponse>('/identity/password/forgot', body)
  return data
}

/**
 * @description Confirms the reset link's token is still usable. Verifying again while it stays
 * usable succeeds the same way and never extends it; every unusable token (unknown, expired,
 * superseded, used, out of attempts) rejects with appCode 1031.
 * @param {VerifyPasswordResetRequest} payload - The token from the /reset-password link.
 * @returns {Promise<VerifyPasswordResetResponse>} Confirmation that the token is usable.
 */
export async function verifyPasswordReset(
  payload: VerifyPasswordResetRequest,
): Promise<VerifyPasswordResetResponse> {
  const { data } = await httpClient.post<VerifyPasswordResetResponse>(
    '/identity/password/reset/verify',
    payload,
  )
  return data
}

/**
 * @description Sets a new password with the reset link's token. The backend re-checks the
 * confirmation match, the shared password policy and the password history (1034, which also
 * counts toward the link's attempt limit).
 * @param {ResetPasswordRequest} payload - The token, the new password and its confirmation.
 * @returns {Promise<ResetPasswordResponse>} Confirmation that the password was reset.
 */
export async function resetPassword(payload: ResetPasswordRequest): Promise<ResetPasswordResponse> {
  const { data } = await httpClient.post<ResetPasswordResponse>('/identity/password/reset', payload)
  return data
}
